import { db } from '@/plugins/firebase';
import { updateDoc, where, query, collection, getDocs, addDoc } from 'firebase/firestore';
import { ActionContext } from 'vuex';
import { RootState } from '@/store/index';
import { Profile, ProfileState } from '@/models/models';

const state: ProfileState = {
    profile: null,
}

const mutations = {
    setProfile(state: ProfileState, profile: Profile) {
        state.profile = profile;
    },
    clearProfile(state: ProfileState) {
        state.profile = null;
    }
};

const actions = {
    async fetchProfile({ commit }: ActionContext<ProfileState, RootState>, uid: string) {
        try {
            const profileRef = collection(db, `users/${uid}/profile`);
            const profileSnapshot = await getDocs(profileRef);

            if (!profileSnapshot.empty) {
                const profileData = {
                    name: profileSnapshot.docs[0].data().name,
                    email: profileSnapshot.docs[0].data().email,
                    payment_info: profileSnapshot.docs[0].data().payment_info,
                    transcribed_count_per_day: profileSnapshot.docs[0].data().transcribed_count_per_day,
                    suggested_count_per_day: profileSnapshot.docs[0].data().suggested_count_per_day,
                    uploaded_files_count_per_day: profileSnapshot.docs[0].data().uploaded_files_count_per_day,
                    membership_plan: profileSnapshot.docs[0].data().membership_plan,
                    last_uploaded_date: profileSnapshot.docs[0].data().last_uploaded_date ? profileSnapshot.docs[0].data().last_uploaded_date.toDate() : null,
                    created_at: profileSnapshot.docs[0].data().created_at,
                }
                commit('setProfile', profileData as Profile);
            } else {
                console.log("Profile not found");
                commit('clearProfile');
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        }
    },

    async updateProfile({ commit }: ActionContext<ProfileState, RootState>, { uid, profile }: { uid: string, profile: Profile }) {
        try {
            const profileRef = collection(db, `users/${uid}/profile`);
            const profileQuery = query(profileRef, where('email', '==', profile.email))
            const profileSnapshot = await getDocs(profileQuery);

            if (!profileSnapshot.empty) {
                const profileData = {
                    name: profile.name,
                    email: profile.email,
                    payment_info: profile.payment_info,
                    transcribed_count_per_day: profile.transcribed_count_per_day,
                    suggested_count_per_day: profile.suggested_count_per_day,
                    uploaded_files_count_per_day: profile.uploaded_files_count_per_day,
                    membership_plan: profile.membership_plan,
                    last_uploaded_date: profile.last_uploaded_date,
                    created_at: profile.created_at,
                }
                await updateDoc(profileSnapshot.docs[0].ref, profileData);
                commit('setProfile', profile);
            } else {
                commit('clearProfile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
        }
    },

    async createProfile({ commit }: ActionContext<ProfileState, RootState>, { uid, profile }: { uid: string, profile: Profile }) {
        try {
            const profileRef = collection(db, `users/${uid}/profile`);
            const profileQuery = query(profileRef, where('email', '==', profile.email))
            const profileSnapshot = await getDocs(profileQuery);

            if (profileSnapshot.empty) {
                await addDoc(profileRef, profile);
                commit('setProfile', profile);
            } else {
                console.log('Profile already exists, skipping creation');
                commit('setProfile', profileSnapshot.docs[0].data() as Profile);
            }
        } catch (error) {
            console.error("Error creating profile:", error);
        }
    },
};

const getters = {
    profile: (state: ProfileState): Profile | null => state.profile,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
